import React from 'react';
import styled from 'styled-components';
import {
  ExperienceVariant,
  ShopifyOrderTag,
} from '@/webapi/use-experience-api';
import { GradientModal } from '@/components/gradient-modal';
import { centered } from '@/components/use-shared-element';
import { Footer } from '@/features/editor/widgets/shared/modals/commons';
import { TextInput } from '@/components/text-input';

export interface ShopifyOrderTagsModalProps {
  isVisible: boolean;
  fromRef: React.MutableRefObject<HTMLElement>;
  experienceName: string;
  variants: ExperienceVariant[];
  tags: ShopifyOrderTag[];
  onClose: () => void;
  onSave: () => void;
  onChange: (variantId: string, label: string) => void;
}

export function ShopifyOrderTagsModal({
  isVisible,
  fromRef,
  experienceName,
  variants,
  tags,
  onClose,
  onSave,
  onChange,
}: ShopifyOrderTagsModalProps) {
  const controlTag = tags.find((tag) => tag.variantId === `original`);

  return (
    <GradientModal
      closeTop="1rem"
      closeRight="1.5rem"
      fromStyle={{
        borderRadius: `2rem`,
        backgroundColor: `#FFFFFF`,
        padding: `2rem 0 0 0`,
      }}
      toStyle={{
        borderRadius: `1rem`,
        backgroundColor: `#FFFFFF`,
        padding: `2rem 0 0 0`,
      }}
      isVisible={isVisible}
      showBackdrop
      targetPosAndSize={centered(60, 60)}
      fromRef={fromRef}
      onClose={onClose}
      header={
        <ShopifyOrderTagsHeader>
          <span>Shopify order tags</span>
          <ShopifyBetaBadge>Beta</ShopifyBetaBadge>
        </ShopifyOrderTagsHeader>
      }
      footer={<Footer onClick={onSave} text="Save" />}
    >
      <ShopifyOrderTagsModalBody>
        <ShopifyOrderTagField>
          <ShopifyOrderTagFieldLabel>Original</ShopifyOrderTagFieldLabel>
          <TextInput
            value={controlTag?.label || ``}
            maxLength={40}
            onChange={(ev) => onChange(`original`, ev.target.value)}
          />
        </ShopifyOrderTagField>
        {variants.map((variant) => {
          const currentTag = tags.find((tag) => tag.variantId === variant.id);
          return (
            <ShopifyOrderTagField key={variant.id}>
              <ShopifyOrderTagFieldLabel>
                {variant.name}
              </ShopifyOrderTagFieldLabel>
              <TextInput
                value={currentTag?.label || ``}
                maxLength={40}
                onChange={(ev) => onChange(variant.id, ev.target.value)}
              />
            </ShopifyOrderTagField>
          );
        })}
        <ShopifyOrderTagsNote>
          Default tag:
          <br />
          {`${experienceName}_<variant name>:<version>:<attribution(user|session)>`}
          <br />
        </ShopifyOrderTagsNote>
      </ShopifyOrderTagsModalBody>
    </GradientModal>
  );
}

const ShopifyOrderTagsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
  font-size: 1.8rem;
  font-weight: 700;
  color: #262d38;
`;

const ShopifyBetaBadge = styled.span`
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #7b5a00;
  background: #fff1c2;
  border: 1px solid #f0d27f;
  border-radius: 999px;
  padding: 0.2rem 0.8rem;
`;

const ShopifyOrderTagsModalBody = styled.div`
  margin: 1.5rem 2rem 2rem;
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.2rem;
`;

const ShopifyOrderTagField = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.6rem;

  input {
    width: 100%;
    box-shadow: none;
    font-size: 1.3rem;
    border-color: #d7dbe0;
  }
`;

const ShopifyOrderTagFieldLabel = styled.span`
  font-size: 1.2rem;
  font-weight: 700;
  color: #566171;
  text-align: left;
`;

const ShopifyOrderTagsNote = styled.div`
  margin-top: 0.8rem;
  font-size: 1.2rem;
  color: #7b8695;
  line-height: 1.5;
  text-align: left;
  border: 1px solid #e1e4e8;
  border-radius: 0.8rem;
  background: #f8fafb;
  padding: 1rem;
`;
